// SubCredit — portable credit score visible to the subscriber.

import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { orpc } from '../lib/orpc.js';
import { current_language } from '../i18n/index.js';
import { friendly_error } from '../lib/friendly-error.js';

interface CreditScore {
  score: number;
  risk_class: 'low' | 'medium' | 'high';
  contributors: Array<{ feature: string; weight: number; reason: string }>;
  tips: string[];
  contributing_tenants: number;
  model_version: string;
}

export function SubCredit(): ReactElement {
  const { t } = useTranslation();
  const lang = current_language();
  const q = useQuery({
    queryKey: ['sub-credit', lang],
    queryFn: () => orpc.sub.credit_score({ locale: lang }),
  });

  if (q.isLoading) return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
  if (q.error) return <div style={{ color: 'var(--bad)', fontSize: 13 }}>{friendly_error(q.error)}</div>;
  if (!q.data) return <></>;
  const r = q.data as CreditScore;

  return (
    <>
      <div>
        <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')} style={{ fontSize: 20 }}>
          {t('sub.credit.title', 'Credit Score')}
        </div>
        <div className="h-sub">
          {t('sub.credit.subtitle', 'Portable across opted-in chit operators. Updates with every on-time payment.')}
        </div>
      </div>

      <ScoreCard score={r.score} risk_class={r.risk_class} />

      {r.contributing_tenants === 0 && (
        <div style={{ background: 'var(--info-soft)', color: 'var(--info)', borderRadius: 'var(--radius-lg)', padding: 14, fontSize: 12 }}>
          {t('sub.credit.no_data', 'No opted-in operator has shared data for you yet. Your score is a neutral baseline.')}
        </div>
      )}

      {r.contributors.length > 0 && (
        <div className="card">
          <div className="card-head"><div className="card-title">{t('sub.credit.factors', 'Score factors')}</div></div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {r.contributors.map((c) => (
              <div key={c.feature} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                <span className={lang === 'ta' ? 'tamil' : ''}>{c.reason}</span>
                <span className="mono" style={{ flexShrink: 0, color: c.weight > 0 ? 'var(--good)' : c.weight < 0 ? 'var(--bad)' : 'var(--ink-3)' }}>
                  {c.weight > 0 ? '+' : ''}{c.weight}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {r.tips.length > 0 && (
        <div className="card">
          <div className="card-head"><div className="card-title">{t('sub.credit.tips', 'How to improve')}</div></div>
          <ul style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, margin: 0, listStyle: 'none' }}>
            {r.tips.map((tip, i) => (
              <li key={i} className={lang === 'ta' ? 'tamil' : ''} style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--primary)', flexShrink: 0 }}>•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', textAlign: 'center' }}>
        {r.contributing_tenants} {t('sub.credit.from_tenants', 'contributing operator(s)')} · {r.model_version}
      </div>
    </>
  );
}

function ScoreCard({
  score,
  risk_class,
}: {
  score: number;
  risk_class: 'low' | 'medium' | 'high';
}): ReactElement {
  const { t } = useTranslation();
  const bg = risk_class === 'low' ? 'var(--good)' : risk_class === 'high' ? 'var(--bad)' : 'var(--warn)';
  return (
    <div className="sub-score" style={{ background: bg }}>
      <div className="lbl">{t('sub.credit.your_score', 'Your Score')}</div>
      <div className="val">{score}</div>
      <div className="scale">/ 100 · {risk_class}</div>
    </div>
  );
}
